import { NavLinkItemType } from "@/layouts/DashboardLayout/NavLink";
import { isAlreadyScopedPath, isExternalOrAnchorHref } from "@/utils/common/scoped-routes";

/**
 * Prefetch registry for lazy-loaded route chunks.
 *
 * - Uses the same `import("...")` specifiers as the matching entries in
 *   [routes.tsx](./routes.tsx), so Vite emits one chunk per page that both the
 *   prefetcher and the lazy route share. Calling either warms the same cache.
 * - Two entry points:
 *   - `prefetchNavLinkPage(type)` — keyed by `NavLinkItemType` (used by the sidebar).
 *   - `prefetchPath(href)` — keyed by URL (used by tables, action menus, popovers, …).
 * - Both share a single `started` set keyed by the factory function itself, so
 *   the same chunk is never re-prefetched whatever route led to it.
 */

type PageFactory = () => Promise<unknown>;

// --- Page factories ---------------------------------------------------------

const DashboardPage: PageFactory = () => import("../pages/common/DashboardPage/DashboardPage");
const DocumentationPage: PageFactory = () => import("../pages/common/DocumentationPage");
const AuditLogsPage: PageFactory = () => import("../pages/auditLog/AuditLogsPage/AuditLogsPage");

const UsersPage: PageFactory = () => import("../pages/user/UsersPage/UsersPage");
const CreateUserPage: PageFactory = () => import("../pages/user/CreateUserPage");
const UpdateUserPage: PageFactory = () => import("../pages/user/UpdateUserPage/UpdateUserPage");

const CreateTournamentCategoryPage: PageFactory = () =>
  import("../pages/tournamentCategory/CreateTournamentCategoryPage");
const UpdateTournamentCategoryPage: PageFactory = () =>
  import("../pages/tournamentCategory/UpdateTournamentCategoryPage");
const BulkUpdateTournamentCategoriesPage: PageFactory = () =>
  import("../pages/tournamentCategory/BulkUpdateTournamentCategoriesPage");

const CreateRankingPointsBlockPage: PageFactory = () =>
  import("../pages/rankingPoint/CreateRankingPointsBlockPage");
const UpdateRankingPointPage: PageFactory = () =>
  import("../pages/rankingPoint/UpdateRankingPointPage");
const RankingPointPage: PageFactory = () => import("../pages/rankingPoint/RankingPointPage");

const OrganizationsPage: PageFactory = () =>
  import("../pages/organization/OrganizationsPage/OrganizationsPage");
const CreateOrganizationPage: PageFactory = () =>
  import("../pages/organization/CreateOrganizationPage/CreateOrganizationPage");
const UpdateOrganizationPage: PageFactory = () =>
  import("../pages/organization/UpdateOrganizationPage/UpdateOrganizationPage");

const TournamentsPage: PageFactory = () =>
  import("../pages/tournament/TournamentsPage/TournamentsPage");
const TournamentMatchesPage: PageFactory = () =>
  import("../pages/tournament/TournamentMatchesPage/TournamentMatchesPage");
const TournamentEntriesPage: PageFactory = () =>
  import("../pages/tournament/TournamentEntriesPage/TournamentEntriesPage");
const TournamentResultsPage: PageFactory = () =>
  import("../pages/tournament/TournamentResultsPage/TournamentResultsPage");
const TournamentTransactionsPage: PageFactory = () =>
  import("../pages/tournament/TournamentTransactionsPage/TournamentTransactionsPage");

const LicensesPage: PageFactory = () => import("../pages/license/LicensesPage/LicensesPage");
const CertificatesPage: PageFactory = () =>
  import("../pages/license/LicensesPage/CertificatesPage");
const ApplicationFormsPage: PageFactory = () =>
  import("../pages/license/LicensesPage/ApplicationFormsPage");
const CreateLicensePage: PageFactory = () =>
  import("../pages/license/CreateLicensePage/CreateLicensePage");
const CreateCertificatePage: PageFactory = () =>
  import("../pages/license/CreateLicensePage/CreateCertificatePage");
const CreateApplicationFormPage: PageFactory = () =>
  import("../pages/license/CreateLicensePage/CreateApplicationFormPage");
const UpdateLicensePage: PageFactory = () =>
  import("../pages/license/UpdateLicensePage/UpdateLicensePage");
const UpdateCertificatePage: PageFactory = () =>
  import("../pages/license/UpdateLicensePage/UpdateCertificatePage");
const UpdateApplicationFormPage: PageFactory = () =>
  import("../pages/license/UpdateLicensePage/UpdateApplicationFormPage");
const PurchasedLicensesPage: PageFactory = () =>
  import("../pages/purchasedLicense/PurchasedLicensesPage/PurchasedLicensesPage");

const LeaguesPage: PageFactory = () => import("../pages/league/LeaguesPage/LeaguesPage");
const LeaguePage: PageFactory = () => import("../pages/league/LeaguePage/LeaguePage");
const LeagueAthletesPage: PageFactory = () => import("../pages/league/LeagueAthletesPage");
const LeagueTeamsPage: PageFactory = () => import("../pages/league/LeagueTeamsPage");
const LeagueRankingsPage: PageFactory = () => import("../pages/league/LeagueRankingsPage");
const LeagueResultsPage: PageFactory = () => import("../pages/league/LeagueResultsPage");
const CreateLeaguePage: PageFactory = () =>
  import("../pages/league/CreateLeaguePage/CreateLeaguePage");
const LeaguesCreateLegacy: PageFactory = () => import("../pages/dashboard/LeagueCreate");

const ClubsPage: PageFactory = () => import("../pages/club/ClubsPage/ClubsPage");
const CoachesPage: PageFactory = () => import("../pages/coach/CoachesPage");
const NewsPage: PageFactory = () => import("../pages/news/NewsPage/NewsPage");
const AlbumsPage: PageFactory = () => import("../pages/album/AlbumsPage");
const PartnersPage: PageFactory = () => import("../pages/partner/PartnersPage/PartnersPage");
const CreatePartnerPage: PageFactory = () =>
  import("../pages/partner/CreatePartnerPage/CreatePartnerPage");
const UpdatePartnerPage: PageFactory = () =>
  import("../pages/partner/UpdatePartnerPage/UpdatePartnerPage");
const PartnerGroupsPage: PageFactory = () =>
  import("../pages/partnerGroup/PartnerGroupsPage/PartnerGroupsPage");
const BannersPage: PageFactory = () => import("../pages/banner/BannersPage/BannersPage");
const CreateBannerPage: PageFactory = () =>
  import("../pages/banner/CreateBannerPage/CreateBannerPage");
const UpdateBannerPage: PageFactory = () =>
  import("../pages/banner/UpdateBannerPage/UpdateBannerPage");
const TransactionsPage: PageFactory = () =>
  import("../pages/transaction/TransactionsPage/TransactionsPage");
const StripeConnectsPage: PageFactory = () =>
  import("../pages/StripeConnectPage/StripeConnectsPage");
const PlatformDocumentsPage: PageFactory = () =>
  import("../pages/platformDocument/PlatformDocumentsPage/PlatformDocumentsPage");
const CreatePlatformDocumentPage: PageFactory = () =>
  import("../pages/platformDocument/CreatePlatformDocumentPage/CreatePlatformDocumentPage");
const UpdatePlatformDocumentPage: PageFactory = () =>
  import("../pages/platformDocument/UpdatePlatformDocumentPage/UpdatePlatformDocumentPage");
const TeamsPage: PageFactory = () => import("../pages/team/TeamsPage/TeamsPage");
const CreateTeamPage: PageFactory = () => import("../pages/team/CreateTeamPage");
const UpdateTeamPage: PageFactory = () => import("../pages/team/UpdateTeamPage");

const ClubsCreate: PageFactory = () => import("../pages/dashboard/ClubsCreate");
const CoachesCreate: PageFactory = () => import("../pages/dashboard/CoachesCreate");

const Platforms: PageFactory = () => import("../pages/platform/PlatformsPage/PlatformsPage");
const PlatformSettingsLayout: PageFactory = () => import("../pages/dashboard/PlatformCreate");
const Sports: PageFactory = () => import("../pages/dashboard/Sports");
const SportsCreate: PageFactory = () => import("../pages/dashboard/SportsCreate");
const Disciplines: PageFactory = () => import("../pages/dashboard/Disciplines");
const ThirdPartyLicense: PageFactory = () => import("../pages/dashboard/thirdPartyLicense");
const Pages: PageFactory = () => import("../pages/dashboard/Pages");
const ManagementTeamPage: PageFactory = () =>
  import("../pages/managementTeam/ManagementTeamPage/ManagementTeamPage");

// --- NavLink-typed prefetchers ---------------------------------------------

const NAV_LINK_PREFETCHERS: Partial<Record<NavLinkItemType, PageFactory>> = {
  home: DashboardPage,
  clubs: ClubsPage,
  venues: ClubsPage,
  leagues: LeaguesPage,
  tournaments: TournamentsPage,
  users: UsersPage,
  coaches: CoachesPage,
  organizations: OrganizationsPage,
  licenses: LicensesPage,
  certificates: CertificatesPage,
  applicationForms: ApplicationFormsPage,
  issuedLicenses: PurchasedLicensesPage,
  issuedCertificates: PurchasedLicensesPage,
  issuedApplicationForms: PurchasedLicensesPage,
  partners: PartnersPage,
  platforms: Platforms,
  sports: Sports,
  disciplines: Disciplines,
  thirdPartyLicenses: ThirdPartyLicense,
  platformSettings: PlatformSettingsLayout,
  organizationSettings: UpdateOrganizationPage,
  userSettings: UpdateUserPage,
  news: NewsPage,
  gallery: AlbumsPage,
  documents: PlatformDocumentsPage,
  pages: Pages,
  banners: BannersPage,
  transactions: TransactionsPage,
  stripeConnect: StripeConnectsPage,
  platformStripeAccounts: StripeConnectsPage,
  teams: TeamsPage,
  managementTeam: ManagementTeamPage,
  auditLogs: AuditLogsPage,
  documentation: DocumentationPage,
};

// --- Path-pattern prefetchers ----------------------------------------------

type PathRule = { test: RegExp; factory: PageFactory };

// Order matters: most-specific first. Each entry matches against the
// scope-stripped pathname (e.g. "/users/edit/42/general").
const PATH_PREFETCHERS: PathRule[] = [
  { test: /^\/users\/new(\/|$)/, factory: CreateUserPage },
  { test: /^\/users\/edit\//, factory: UpdateUserPage },
  { test: /^\/users(\/|$)/, factory: UsersPage },

  { test: /^\/licenses\/new(\/|$)/, factory: CreateLicensePage },
  { test: /^\/licenses\/edit\//, factory: UpdateLicensePage },
  { test: /^\/licenses(\/|$)/, factory: LicensesPage },
  { test: /^\/certificates\/new(\/|$)/, factory: CreateCertificatePage },
  { test: /^\/certificates\/edit\//, factory: UpdateCertificatePage },
  { test: /^\/certificates(\/|$)/, factory: CertificatesPage },
  { test: /^\/application-forms\/new(\/|$)/, factory: CreateApplicationFormPage },
  { test: /^\/application-forms\/edit\//, factory: UpdateApplicationFormPage },
  { test: /^\/application-forms(\/|$)/, factory: ApplicationFormsPage },
  { test: /^\/issued-licenses(\/|$)/, factory: PurchasedLicensesPage },
  { test: /^\/issued-certificates(\/|$)/, factory: PurchasedLicensesPage },
  { test: /^\/submitted-application-forms(\/|$)/, factory: PurchasedLicensesPage },

  { test: /^\/entities\/new(\/|$)/, factory: CreateOrganizationPage },
  { test: /^\/entities\/edit\//, factory: UpdateOrganizationPage },
  { test: /^\/entities(\/|$)/, factory: OrganizationsPage },

  { test: /^\/tournament\/[^/]+\/transactions(\/|$)/, factory: TournamentTransactionsPage },
  { test: /^\/tournament\/[^/]+\/results(\/|$)/, factory: TournamentResultsPage },
  { test: /^\/tournament\/[^/]+\/entries(\/|$)/, factory: TournamentEntriesPage },
  // A bare `/tournament/:id` is intentionally absent: it hands off to the
  // Manager app (see `redirectTournamentRootToManager`), so there is no Admin
  // chunk worth warming for it.
  { test: /^\/tournament\/[^/]+\/matches(\/|$)/, factory: TournamentMatchesPage },
  { test: /^\/tournaments(\/|$)/, factory: TournamentsPage },

  { test: /^\/league\/[^/]+\/athletes(\/|$)/, factory: LeagueAthletesPage },
  { test: /^\/league\/[^/]+\/teams(\/|$)/, factory: LeagueTeamsPage },
  { test: /^\/league\/[^/]+\/results(\/|$)/, factory: LeagueResultsPage },
  {
    test: /^\/league\/[^/]+\/tournament-category\/new(\/|$)/,
    factory: CreateTournamentCategoryPage,
  },
  {
    test: /^\/league\/[^/]+\/tournament-category\/edit\//,
    factory: UpdateTournamentCategoryPage,
  },
  {
    test: /^\/league\/[^/]+\/tournament-category\/bulk-edit(\/|$)/,
    factory: BulkUpdateTournamentCategoriesPage,
  },
  {
    test: /^\/league\/[^/]+\/ranking-point-block\/new(\/|$)/,
    factory: CreateRankingPointsBlockPage,
  },
  {
    test: /^\/league\/[^/]+\/ranking-point-block\/edit\//,
    factory: UpdateRankingPointPage,
  },
  { test: /^\/league\/[^/]+\/rankings(\/|$)/, factory: LeagueRankingsPage },
  { test: /^\/league\/[^/]+\/ranking-point\/edit\//, factory: RankingPointPage },
  { test: /^\/league\/[^/]+(\/|$)/, factory: LeaguePage },
  { test: /^\/tours\/new(\/|$)/, factory: CreateLeaguePage },
  { test: /^\/leagues\/new(\/|$)/, factory: CreateLeaguePage },
  { test: /^\/tours\/(edit|settings)(\/|$)/, factory: LeaguesCreateLegacy },
  { test: /^\/tours(\/|$)/, factory: LeaguesPage },
  { test: /^\/platform-leagues(\/|$)/, factory: LeaguesPage },
  { test: /^\/organization_leagues(\/|$)/, factory: LeaguesPage },

  { test: /^\/operators\/(new|edit)(\/|$)/, factory: ClubsCreate },
  { test: /^\/operators(\/|$)/, factory: ClubsPage },
  { test: /^\/coaches\/new(\/|$)/, factory: CoachesCreate },
  { test: /^\/coaches(\/|$)/, factory: CoachesPage },

  { test: /^\/teams\/new(\/|$)/, factory: CreateTeamPage },
  { test: /^\/teams\/edit\//, factory: UpdateTeamPage },
  { test: /^\/teams(\/|$)/, factory: TeamsPage },

  { test: /^\/partners\/groups(\/|$)/, factory: PartnerGroupsPage },
  { test: /^\/partners\/new(\/|$)/, factory: CreatePartnerPage },
  { test: /^\/partners\/edit\//, factory: UpdatePartnerPage },
  { test: /^\/partners(\/|$)/, factory: PartnersPage },

  { test: /^\/banners\/new(\/|$)/, factory: CreateBannerPage },
  { test: /^\/banners\/edit\//, factory: UpdateBannerPage },
  { test: /^\/banners(\/|$)/, factory: BannersPage },

  { test: /^\/documents\/new(\/|$)/, factory: CreatePlatformDocumentPage },
  { test: /^\/documents\/edit\//, factory: UpdatePlatformDocumentPage },
  { test: /^\/documents(\/|$)/, factory: PlatformDocumentsPage },

  { test: /^\/news(\/|$)/, factory: NewsPage },
  { test: /^\/albums(\/|$)/, factory: AlbumsPage },
  { test: /^\/transactions(\/|$)/, factory: TransactionsPage },
  { test: /^\/stripe-accounts(\/|$)/, factory: StripeConnectsPage },
  { test: /^\/platform-stripe-accounts(\/|$)/, factory: StripeConnectsPage },

  { test: /^\/platforms\/manage\//, factory: PlatformSettingsLayout },
  { test: /^\/platforms(\/|$)/, factory: Platforms },
  { test: /^\/sports\/(new|edit)(\/|$)/, factory: SportsCreate },
  { test: /^\/sports(\/|$)/, factory: Sports },
  { test: /^\/platform-sports(\/|$)/, factory: Sports },
  { test: /^\/disciplines(\/|$)/, factory: Disciplines },
  { test: /^\/platform-disciplines(\/|$)/, factory: Disciplines },
  { test: /^\/third-party-license(\/|$)/, factory: ThirdPartyLicense },
  { test: /^\/platform-third-party-license(\/|$)/, factory: ThirdPartyLicense },
  { test: /^\/pages(\/|$)/, factory: Pages },

  { test: /^\/management-team(\/|$)/, factory: ManagementTeamPage },
  { test: /^\/audit-logs(\/|$)/, factory: AuditLogsPage },
  { test: /^\/documentation(\/|$)/, factory: DocumentationPage },

  { test: /^\/?$/, factory: DashboardPage },
  { test: /^\/statistics(\/|$)/, factory: DashboardPage },
];

// --- Runtime ----------------------------------------------------------------

const started = new WeakSet<PageFactory>();

const run = (factory: PageFactory | undefined) => {
  if (!factory) return;
  if (started.has(factory)) return;
  started.add(factory);
  factory().catch(() => {
    // Allow retry on transient network failure.
    started.delete(factory);
  });
};

export const prefetchNavLinkPage = (type: NavLinkItemType) => {
  run(NAV_LINK_PREFETCHERS[type]);
};

/**
 * Semantic prefetchers for use at render time — call from `usePrefetchOnMount`
 * (or directly) when a page knows what the user will navigate to next.
 *
 * Prefer these over `prefetchPath("/some/static/href")` so renames stay
 * type-safe and grep-able.
 */
export const prefetchPage = {
  // List pages — use these from create/update pages so the "back to list"
  // breadcrumb is instant.
  usersList: () => run(UsersPage),
  organizationsList: () => run(OrganizationsPage),
  licensesList: () => run(LicensesPage),
  certificatesList: () => run(CertificatesPage),
  applicationFormsList: () => run(ApplicationFormsPage),
  teamsList: () => run(TeamsPage),
  clubsList: () => run(ClubsPage),
  coachesList: () => run(CoachesPage),
  tournamentsList: () => run(TournamentsPage),
  leaguesList: () => run(LeaguesPage),

  // Create / edit pages — use these from list pages.
  userCreate: () => run(CreateUserPage),
  userEdit: () => run(UpdateUserPage),
  tournamentCategoryCreate: () => run(CreateTournamentCategoryPage),
  tournamentCategoryEdit: () => run(UpdateTournamentCategoryPage),
  tournamentCategoriesBulkEdit: () => run(BulkUpdateTournamentCategoriesPage),
  rankingPointsBlockCreate: () => run(CreateRankingPointsBlockPage),
  rankingPointsBlockEdit: () => run(UpdateRankingPointPage),
  licenseCreate: () => run(CreateLicensePage),
  licenseEdit: () => run(UpdateLicensePage),
  certificateCreate: () => run(CreateCertificatePage),
  certificateEdit: () => run(UpdateCertificatePage),
  applicationFormCreate: () => run(CreateApplicationFormPage),
  applicationFormEdit: () => run(UpdateApplicationFormPage),
  purchasedLicense: () => run(PurchasedLicensesPage),
  organizationCreate: () => run(CreateOrganizationPage),
  organizationEdit: () => run(UpdateOrganizationPage),
  tournamentMatches: () => run(TournamentMatchesPage),
  tournamentEntries: () => run(TournamentEntriesPage),
  tournamentResults: () => run(TournamentResultsPage),
  tournamentTransactions: () => run(TournamentTransactionsPage),
  leaguePage: () => run(LeaguePage),
  leagueAthletes: () => run(LeagueAthletesPage),
  leagueTeams: () => run(LeagueTeamsPage),
  leagueResults: () => run(LeagueResultsPage),
  leagueCreate: () => run(CreateLeaguePage),
  leagueEditLegacy: () => run(LeaguesCreateLegacy),
  teamCreate: () => run(CreateTeamPage),
  teamEdit: () => run(UpdateTeamPage),
  partnerCreate: () => run(CreatePartnerPage),
  partnerEdit: () => run(UpdatePartnerPage),
  bannerCreate: () => run(CreateBannerPage),
  bannerEdit: () => run(UpdateBannerPage),
  platformsList: () => run(Platforms),
  platformSettings: () => run(PlatformSettingsLayout),
  platformDocumentCreate: () => run(CreatePlatformDocumentPage),
  platformDocumentEdit: () => run(UpdatePlatformDocumentPage),
  clubCreate: () => run(ClubsCreate),
  coachCreate: () => run(CoachesCreate),
  // Already-list-named alias kept for the older sidebar / hover wiring.
  coachesPage: () => run(CoachesPage),
};

/**
 * Strips the scope prefix for all three URL shapes — platform-only
 * (`/platform/:p`), org (`/platform/:p/entity/:o`), and league
 * (`/platform/:p/entity/:o/league/:l`). Ids are public-id strings (e.g. `pf_…`,
 * `fed_…`, `lge_…`), not numeric, so each segment matches `[^/]+`.
 */
const SCOPE_PREFIX = /^\/platform\/[^/]+(\/entity\/[^/]+(\/league\/[^/]+)?)?/;

const stripScope = (path: string): string =>
  isAlreadyScopedPath(path) ? path.replace(SCOPE_PREFIX, "") || "/" : path;

export const prefetchPath = (href: string | null | undefined) => {
  if (!href) return;
  if (isExternalOrAnchorHref(href)) return;
  if (!href.startsWith("/")) return;
  // Drop query string + hash so they don't break the regex tests.
  const [pathOnly] = href.split(/[?#]/);
  const path = stripScope(pathOnly);
  const rule = PATH_PREFETCHERS.find((r) => r.test.test(path));
  run(rule?.factory);
};
